// AG Grid picks row and header heights for touch vs pointer at grid creation, so a
// phone that rotates keeps the heights of the previous orientation (rows drawn 40px
// tall but positioned 20px apart). Kept in its own module so the maths and the
// preserve-dragged-rows ordering can be checked without a browser — see
// rowheight.selfcheck.mjs.

// iPad landscape is 1024px wide; stopping at 768 meant tablets fell into the
// desktop 20px rows, so cell text (13px / 14px line-height on top of ag-grid's
// own padding) overflowed and clipped. Keep touch sizing all the way through
// iPad landscape so rows actually hold their content on tablet. 27px rows hold the
// 20px touch checkbox and a line of cell text; 40px wasted a third of every
// phone/tablet grid on padding (#10186). This is the only place touch row height
// is set: aggrid.css must not force a different row height, or rows overlap.
export const TOUCH_MAX_WIDTH = 1024;

export const isTouchSized = (width) => width <= TOUCH_MAX_WIDTH;
export const headerHeightFor = (width) => (isTouchSized(width) ? 25 : 27);
export const rowHeightFor = (width) => (isTouchSized(width) ? 27 : 20);

// Returns false when the grid is gone, so callers can drop it from their registry.
// Flags on the api itself (`__smaacPreserveRowHeight`, `__smaacPreserveHeaderHeight`)
// let a grid opt out when it declared its own numeric rowHeight / headerHeight at
// construction which the viewport default would otherwise overwrite on first data render).
export function applyViewportRowHeights(gridApi, width) {
  if (!gridApi || gridApi.isDestroyed?.()) return false;

  const touchHeader = !gridApi.__smaacPreserveHeaderHeight;
  const touchRow = !gridApi.__smaacPreserveRowHeight;

  // Read the dragged heights first: changing the rowHeight option already re-measures
  // rows, so capturing afterwards records the new default and "restores" that instead.
  const dragged = [];
  if (touchRow) {
    gridApi.forEachNode?.((node) => {
      if (node.__smaacManualRowHeight) dragged.push([node, node.rowHeight]);
    });
  }

  if (touchHeader)
    gridApi.setGridOption("headerHeight", headerHeightFor(width));
  if (touchRow) {
    gridApi.setGridOption("rowHeight", rowHeightFor(width));
    // resetRowHeights() drops every explicit height, including the ones the user dragged
    // out via bindManualRowResize, so put those back afterwards.
    gridApi.resetRowHeights?.();
    dragged.forEach(([node, height]) => node.setRowHeight(height));
    if (dragged.length) gridApi.onRowHeightChanged?.();
  }
  return true;
}

// Re-apply the touch/pointer row and header heights when the viewport crosses the
// breakpoint (a phone rotating). One window listener for the page, not one per grid:
// htmx swaps grids out without always destroying them, so per-grid listeners pile up
// and keep dead APIs alive. The SSR grids and the React entry grids both register here.
const responsiveHeightGrids = new Set();
let responsiveWasTouchSized = null;

function applyResponsiveRowHeights() {
  if (isTouchSized(window.innerWidth) === responsiveWasTouchSized) return;
  responsiveWasTouchSized = isTouchSized(window.innerWidth);
  responsiveHeightGrids.forEach(function (gridApi) {
    const el = gridApi.getGridElement?.();
    const gone = el && !el.isConnected;
    if (gone || !applyViewportRowHeights(gridApi, window.innerWidth)) {
      responsiveHeightGrids.delete(gridApi);
    }
  });
}

export function bindResponsiveRowHeight(gridApi) {
  if (!gridApi) return;
  responsiveHeightGrids.add(gridApi);
  gridApi.addEventListener('gridPreDestroyed', function () {
    responsiveHeightGrids.delete(gridApi);
  });
  if (responsiveWasTouchSized === null) {
    responsiveWasTouchSized = isTouchSized(window.innerWidth);
    window.addEventListener('resize', applyResponsiveRowHeights);
  }
}
